"use client";
import type { BadgeProps } from "../../Badge";
import { Badge } from "../../Badge";
import { Skeleton } from "../../Skeleton";
import { Tooltip } from "../../Tooltip";
import type { CellComponentProps } from "../DataTable.types";
import { getCellContainerClasses, getSkeletonSize } from "./cellUtils";

export interface BadgeCellProps<T = any> extends CellComponentProps<T> {
  /** The variant to use - can be static or a function that maps value to variant */
  variant?: BadgeProps["variant"] | ((value: any, row: T) => BadgeProps["variant"]);

  /** Optional label override - if not provided, will use the value */
  label?: string | ((value: any, row: T) => string);

  /** Badge size */
  size?: BadgeProps["size"];

  /** Badge shape */
  shape?: BadgeProps["shape"];

  /** Whether to show a dot indicator */
  dot?: boolean | ((value: any, row: T) => boolean);

  /** Position of the dot */
  dotPosition?: BadgeProps["dotPosition"];

  /** Whether the dot should pulse */
  dotPulse?: boolean | ((value: any, row: T) => boolean);

  /** Alignment of the cell content */
  align?: "left" | "center" | "right";

  /** Text to show for null/undefined values */
  emptyText?: string;

  /** Additional CSS classes */
  className?: string;

  /**
   * Maximum number of badges to show when value is an array.
   * Remaining items will be shown as "+N" with a tooltip.
   * Defaults to 2.
   */
  maxVisible?: number;

  /**
   * Maximum character length for each badge label.
   * Longer labels will be truncated with "..." and show full text in tooltip.
   * Defaults to undefined (no truncation).
   */
  maxLabelLength?: number;
}

/**
 * BadgeCell
 *
 * A flexible DataTable cell component for displaying any value as a Badge.
 * Supports mapping values to semantic variants (success, error, warning, etc.)
 * and customizing the display label.
 *
 * Also supports array values (e.g., tags) with truncation via maxVisible prop.
 *
 * @example
 * ```tsx
 * // Simple usage with static variant
 * {
 *   id: "status",
 *   label: "Status",
 *   accessor: "status",
 *   cell: BadgeCell,
 *   cellProps: {
 *     variant: "success",
 *   }
 * }
 *
 * // Map value to variant
 * {
 *   id: "status",
 *   label: "Status",
 *   accessor: "status",
 *   cell: BadgeCell,
 *   cellProps: {
 *     variant: (value) => {
 *       if (value === "active") return "success";
 *       if (value === "error") return "error";
 *       if (value === "pending") return "warning";
 *       return "neutral";
 *     },
 *     label: (value) => value.toUpperCase(),
 *   }
 * }
 *
 * // With dot indicator
 * {
 *   id: "online",
 *   label: "Status",
 *   accessor: "onlineStatus",
 *   cell: BadgeCell,
 *   cellProps: {
 *     variant: (value) => value === "online" ? "success" : "neutral",
 *     dot: true,
 *     dotPulse: (value) => value === "online",
 *   }
 * }
 *
 * // Array of tags with truncation
 * {
 *   id: "tags",
 *   label: "Tags",
 *   accessor: (row) => row.tags,
 *   cell: BadgeCell,
 *   cellProps: {
 *     variant: "secondary",
 *     shape: "pill",
 *     maxVisible: 2,
 *   }
 * }
 * ```
 */
export function BadgeCell<T = any>({
  value,
  row,
  context,
  variant = "default",
  label,
  size = "sm",
  shape = "square",
  dot,
  dotPosition = "left",
  dotPulse,
  align = "left",
  emptyText = "—",
  className,
  maxVisible = 2,
  maxLabelLength,
}: BadgeCellProps<T>) {
  const { isLoading } = context;

  // Helper to truncate label if needed
  const truncateLabel = (text: string, fullText: string) => {
    if (!maxLabelLength || text.length <= maxLabelLength) {
      return <>{text}</>;
    }
    const truncated = text.slice(0, maxLabelLength - 1) + "…";
    return (
      <Tooltip content={fullText}>
        <span>{truncated}</span>
      </Tooltip>
    );
  };

  if (isLoading) {
    return <Skeleton className={getSkeletonSize("normal")} />;
  }

  // Handle empty values
  if (value === null || value === undefined || value === "") {
    return <div className={getCellContainerClasses(align, "text-text-muted")}>{emptyText}</div>;
  }

  // Handle empty arrays
  if (Array.isArray(value) && value.length === 0) {
    return <div className={getCellContainerClasses(align, "text-text-muted")}>{emptyText}</div>;
  }

  const containerClasses = getCellContainerClasses(align, className);

  // Handle array values (e.g., tags)
  if (Array.isArray(value)) {
    const visibleItems = value.slice(0, maxVisible);
    const hiddenItems = value.slice(maxVisible);
    const hasMore = hiddenItems.length > 0;

    return (
      <div className={containerClasses}>
        <div className="flex items-center gap-1">
          {visibleItems.map((item, index) => {
            const resolvedVariant = typeof variant === "function" ? variant(item, row) : variant;
            const fullLabel =
              typeof label === "function" ? label(item, row) : label !== undefined ? label : String(item);
            const resolvedDot = typeof dot === "function" ? dot(item, row) : dot;
            const resolvedDotPulse = typeof dotPulse === "function" ? dotPulse(item, row) : dotPulse;

            return (
              <span key={index} className="flex-shrink-0 whitespace-nowrap">
                <Badge
                  variant={resolvedVariant}
                  size={size}
                  shape={shape}
                  dot={resolvedDot}
                  dotPosition={dotPosition}
                  dotPulse={resolvedDotPulse}
                >
                  {truncateLabel(fullLabel, fullLabel)}
                </Badge>
              </span>
            );
          })}
          {hasMore && (
            <Tooltip content={hiddenItems.join(", ")}>
              <span className="flex-shrink-0 whitespace-nowrap">
                <Badge variant="neutral" size={size} shape={shape}>
                  +{hiddenItems.length}
                </Badge>
              </span>
            </Tooltip>
          )}
        </div>
      </div>
    );
  }

  // Single value (original behavior)
  const resolvedVariant = typeof variant === "function" ? variant(value, row) : variant;
  const fullLabel = typeof label === "function" ? label(value, row) : label !== undefined ? label : String(value);
  const resolvedDot = typeof dot === "function" ? dot(value, row) : dot;
  const resolvedDotPulse = typeof dotPulse === "function" ? dotPulse(value, row) : dotPulse;

  return (
    <div className={containerClasses}>
      <Badge
        variant={resolvedVariant}
        size={size}
        shape={shape}
        dot={resolvedDot}
        dotPosition={dotPosition}
        dotPulse={resolvedDotPulse}
      >
        {truncateLabel(fullLabel, fullLabel)}
      </Badge>
    </div>
  );
}
